import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';
import { 
  Smartphone, 
  Search, 
  Filter, 
  Eye, 
  UserX, 
  UserCheck,
  Download,
  Calendar,
  Mail,
  Phone
} from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const ListaUsuariosAplicativo = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    status: '',
    busca: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const carregarUsuarios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('usuario')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setUsuarios(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários do aplicativo:', error);
      toast.error('Erro ao carregar usuários do aplicativo');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatusUsuario = async (id, ativo) => {
    try {
      const { error } = await supabase
        .from('usuario')
        .update({ ativo })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Usuário ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
      
      // Atualizar estado local
      setUsuarios(prev => 
        prev.map(usuario => 
          usuario.id === id ? { ...usuario, ativo } : usuario
        )
      );
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  // Aplicar filtros
  const usuariosFiltrados = usuarios.filter(usuario => {
    // Filtro por status
    if (filtros.status === 'ativo' && !usuario.ativo) return false;
    if (filtros.status === 'inativo' && usuario.ativo) return false;
    
    // Filtro por busca
    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      return (
        usuario.nome?.toLowerCase().includes(busca) ||
        usuario.email?.toLowerCase().includes(busca) ||
        usuario.celular?.includes(busca) ||
        usuario.cpf?.includes(busca)
      );
    }
    
    return true;
  });

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      status: '',
      busca: ''
    });
  };

  const handleToggleStatus = async (id, ativo) => {
    if (window.confirm(`Tem certeza que deseja ${ativo ? 'ativar' : 'desativar'} este usuário?`)) {
      await toggleStatusUsuario(id, ativo);
    }
  };

  const exportarParaExcel = () => {
    const dadosExportacao = usuariosFiltrados.map(usuario => ({
      'Nome': usuario.nome || '',
      'Email': usuario.email || '',
      'Telefone': usuario.celular || '',
      'CPF': usuario.cpf || '',
      'Endereço': usuario.endereco || '',
      'Status': usuario.ativo ? 'Ativo' : 'Inativo',
      'Data de Cadastro': new Date(usuario.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }));

    const ws = XLSX.utils.json_to_sheet(dadosExportacao);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuários Aplicativo');

    const dataAtual = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `usuarios_aplicativo_${dataAtual}.xlsx`);
  };

  if (loading) {
    return (
      <div className="gestao-usuarios lista-full-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando usuários do aplicativo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios lista-full-container">
      <div className="lista-header">
        <div className="header-content">
          <h1>
            <Smartphone size={32} />
            Usuários do Aplicativo
          </h1>
          <p>Usuários cadastrados no aplicativo móvel</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={exportarParaExcel}
            className="button secondary"
            disabled={usuariosFiltrados.length === 0}
          >
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="lista-content-wrapper">
        {/* Barra de busca e filtros */}
        <div className="search-filter-bar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, email, telefone ou CPF..."
              value={filtros.busca}
              onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
            />
          </div>
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
          </button>
        </div>

        {/* Painel de filtros */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <label>Status</label>
                <select name="status" value={filtros.status} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  <option value="ativo">Ativos</option>
                  <option value="inativo">Inativos</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="button secondary" onClick={limparFiltros}>
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Estatísticas rápidas */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{usuariosFiltrados.length}</span>
          </div>
          <div className="stat-item ativo">
            <span className="stat-label">Ativos:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => u.ativo).length}</span>
          </div>
          <div className="stat-item inativo">
            <span className="stat-label">Inativos:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => !u.ativo).length}</span>
          </div>
        </div>

        {/* Lista de usuários */}
        {usuariosFiltrados.length === 0 ? (
          <div className="empty-state">
            <Smartphone size={48} />
            <h3>Nenhum usuário encontrado</h3>
            <p>Não há usuários do aplicativo que correspondam aos filtros aplicados.</p>
          </div>
        ) : (
          <div className="usuarios-list-vertical">
            {usuariosFiltrados.map(usuario => (
              <div key={usuario.id} className="usuario-card-horizontal">
                <div className="card-main-content">
                  <div className="card-header-section">
                    <div className="usuario-info">
                      <h3 className="usuario-title">{usuario.nome || 'Nome não informado'}</h3>
                      <div className="usuario-meta">
                        {usuario.email && (
                          <span className="email">
                            <Mail size={14} />
                            {usuario.email}
                          </span>
                        )}
                        {usuario.celular && (
                          <span className="telefone">
                            <Phone size={14} />
                            {usuario.celular}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="status-badges">
                      <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>

                  <div className="card-body-section">
                    <div className="info-line">
                      <span className="info-label">Cadastrado:</span>
                      <span className="info-value data-criacao">
                        <Calendar size={14} />
                        {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    {usuario.cpf && (
                      <div className="info-line">
                        <span className="info-label">CPF:</span>
                        <span className="info-value">{usuario.cpf}</span>
                      </div>
                    )}
                    {usuario.endereco && (
                      <div className="info-line">
                        <span className="info-label">Endereço:</span>
                        <span className="info-value">{usuario.endereco}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-actions-vertical">
                  <Link 
                    to={`/gestao-usuarios/populacao/aplicativo/detalhes/${usuario.id}`}
                    className="action-button-full view"
                  >
                    <Eye size={16} />
                    Ver Detalhes
                  </Link>
                  
                  <button
                    onClick={() => handleToggleStatus(usuario.id, !usuario.ativo)}
                    className={`action-button-full ${usuario.ativo ? 'deactivate' : 'activate'}`}
                  >
                    {usuario.ativo ? (
                      <>
                        <UserX size={16} />
                        Desativar
                      </>
                    ) : (
                      <>
                        <UserCheck size={16} />
                        Ativar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaUsuariosAplicativo;